/**
 * The nine note states from `docs/architecture.md` §4. A note upload's response (and later,
 * the note list and detail screens) all show one of these, and CLAUDE.md requires the Spanish
 * words to be centralized rather than repeated per screen: "the note's status is always
 * visible later (enviada / procesando / por revisar / bloqueada / lista para Excel / enviada
 * a Excel / error)". `delivering` and `delivery_attention` are the two states that short list
 * does not spell out; `docs/architecture.md` §4 names them and Phase 6's cross-cutting rule
 * requires distinguishing them too ("processing/review/blocked/ready/delivering/exported/
 * failed/attention states in plain language").
 */
export const NOTE_STATUSES = [
  'submitted',
  'processing',
  'awaiting_review',
  'blocked_reference',
  'ready_for_excel',
  'delivering',
  'exported',
  'delivery_attention',
  'failed',
] as const;

export type NoteStatus = (typeof NOTE_STATUSES)[number];

export const NOTE_STATUS_LABELS: Record<NoteStatus, string> = {
  submitted: 'Enviada',
  processing: 'Procesando',
  awaiting_review: 'Por revisar',
  blocked_reference: 'Bloqueada',
  ready_for_excel: 'Lista para Excel',
  delivering: 'Enviando a Excel',
  exported: 'Enviada a Excel',
  delivery_attention: 'Necesita atención',
  failed: 'Error',
};

/**
 * Statuses the system can still move on its own, with no owner action in between - the note
 * list polls while one of these is on screen (`docs/implementation/06-mobile-web.md` 6.3).
 * `awaiting_review`, `blocked_reference`, `failed`, and `delivery_attention` all wait on the
 * owner; `exported` is final. Everything here can change from a background worker or the
 * Windows agent alone.
 */
export const PROCESSING_LIKE_STATUSES: readonly NoteStatus[] = [
  'submitted',
  'processing',
  'ready_for_excel',
  'delivering',
];

export function isProcessingLike(status: NoteStatus): boolean {
  return PROCESSING_LIKE_STATUSES.includes(status);
}

/** Mirrors `backend/app/domain/enums.py`'s `FailureCode`: why recognition could not produce a
 * reviewable result. Safe to show the owner; the diagnostic detail is never sent to the browser. */
export const FAILURE_CODES = [
  'image_unreadable',
  'provider_unavailable',
  'provider_rejected',
  'invalid_model_output',
  'internal_error',
] as const;

export type FailureCode = (typeof FAILURE_CODES)[number];

export const FAILURE_CODE_LABELS: Record<FailureCode, string> = {
  image_unreadable: 'La foto no se pudo leer bien. Vuelve a tomarla con mejor luz y sin borrones.',
  provider_unavailable: 'El servicio está saturado. Intenta más tarde.',
  provider_rejected: 'El servicio de reconocimiento rechazó la foto.',
  invalid_model_output: 'No se pudo interpretar el resultado del reconocimiento.',
  internal_error: 'Ocurrió un error interno al procesar la nota.',
};
